import background from '../../../assets/detail-bg.svg';
import Select, { StylesConfig } from 'react-select';
import { NavBar, NavBarItem } from '../../navbar';
import { useNavigate, useParams } from 'react-router-dom';
import ReactModal from 'react-modal';
import { useEffect, useState } from 'react';
import { getExactDate } from '../../../utils';
import { ActivityInfo, TaskData } from '../../../models';
import { useActivityData } from '../UseActivityData';
import { MissionCalendar } from './MissionCalendar';
import { TaskCard } from './TaskCard';
import { FeedbackModal } from './FeedbackModal';
import { getMemberDetail } from '../../../utils/api-calls/getMemberDetail';
import { ShareFeedbackModal } from './ShareFeedbackModal';
import { useTaskResult } from '../../UseTaskResult';
import { useTranslation } from 'react-i18next';

export default function MemberDetail() {
  const [t] = useTranslation(['chatroom']);
  const [isFeedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackModalData, setFeedbackModalData] = useState<TaskData>();

  const [isShareModalOpen, setShareModalOpen] = useState(false);
  const [shareModalData, setShareModalData] = useState<TaskData>();

  const { memberId } = useParams();
  const { activityInfo, room } = useActivityData();

  const [selectedDay, setSelectedDay] = useState<number>();
  const [today, setToday] = useState<number>();
  const navigate = useNavigate();

  const setSelectedDayWrapper = (day: number) => {
    setSelectedDay(day);
  };

  const openFeedbackModal = (data: TaskData) => {
    setFeedbackModalData(() => data);
    setFeedbackModalOpen(() => true);
  };

  const closeFeedbackModal = (goShare: boolean, feedback?: string) => {
    if (goShare && feedback && feedbackModalData) {
      setShareModalData(() => {
        return {
          ...feedbackModalData,
          feedback,
        };
      });

      getMemberDetail(memberId!, room!, selectedDay!).then((data) => {
        setTaskResult(data.result);
        setFeedbackModalOpen(() => false);
        setFeedbackModalData(() => undefined);
        setShareModalOpen(() => true);
      });

      return;
    } else {
      setFeedbackModalData(() => undefined);
      setShareModalData(() => undefined);
      setFeedbackModalOpen(() => false);
    }
  };

  const closeShareModal = () => {
    setShareModalOpen(() => false);
  };

  useEffect(() => {
    if (!room) {
      return;
    }
    const { duration, day } = room.activity;
    setToday(() => day);
    setSelectedDay(() => Math.min(duration, day));
  }, [room]);

  const [taskResult, setTaskResult] = useTaskResult(
    memberId,
    room,
    selectedDay
  );
  const selectMemberOptions = useSelectMemberOptions(activityInfo);

  const style = {
    backgroundImage: `url(${background})`,
  };
  const selectStyles: StylesConfig<any> = {
    dropdownIndicator: (styles) => ({ ...styles, color: '#54A178' }),
  };

  const selectMember = (selected: { value: string; label: string }) => {
    navigate(`../${selected.value}`, {
      replace: true,
      relative: 'path',
    });
  };

  return (
    <div className="relative">
      <NavBar title={NavBarItem.MEMBER_DETAIL} />
      <div className="h-[calc(100vh_-_3rem)] flex flex-col">
        <div className="bg-manage-primary p-5">
          <Select
            isSearchable={false}
            options={selectMemberOptions}
            className="mb-4"
            styles={selectStyles}
            value={selectMemberOptions.find((o) => o.value === memberId)}
            onChange={selectMember}
          />
          <MissionCalendar
            duration={room?.activity.duration!}
            today={today!}
            selectedDay={selectedDay!}
            updateCurrentDay={setSelectedDayWrapper}
          />
        </div>
        <div
          style={style}
          className="flex-grow bg-no-repeat bg-cover px-4 py-6 overflow-y-scroll"
        >
          <div className="flex flex-col">
            <div>
              <span className="font-bold text-2xl text-manage-primary mr-2">
                {t('activity_record_day', '第 {{selectedDay}} 天', {
                  selectedDay: selectedDay,
                })}
              </span>
              <span>{getExactDate(selectedDay!, today!)}</span>
            </div>
            {taskResult.map((data) => (
              <TaskCard
                key={data.id}
                data={data}
                openFeedbackModal={openFeedbackModal}
              />
            ))}
          </div>
        </div>
      </div>

      <ReactModal
        isOpen={isFeedbackModalOpen}
        overlayClassName="z-[51] fixed bg-black/[.4] inset-0 backdrop-blur-sm"
        className="flex h-full w-full justify-center items-center px-5"
      >
        <FeedbackModal
          taskData={feedbackModalData!}
          closeFeedbackModal={closeFeedbackModal}
        />
      </ReactModal>

      <ReactModal
        isOpen={isShareModalOpen}
        onRequestClose={() => setShareModalOpen(() => false)}
        overlayClassName="z-[51] fixed bg-black/[.4] inset-0 backdrop-blur-sm"
        className="flex h-full w-full justify-center items-center px-5"
      >
        <ShareFeedbackModal
          taskData={shareModalData!}
          handleCloseClick={closeShareModal}
        />
      </ReactModal>
    </div>
  );
}

function useSelectMemberOptions(activityInfo: ActivityInfo | undefined) {
  const [options, setOptions] = useState<{ value: string; label: string }[]>(
    []
  );

  useEffect(() => {
    if (!activityInfo) {
      return;
    }

    setOptions(() =>
      activityInfo.members.map((member) => ({
        value: member.id,
        label: member.name,
      }))
    );
  }, [activityInfo]);

  return options;
}
